import { z } from 'zod';

/**
 * Social / contact links shown in the storefront footer. Staff choose which ones to show and paste each link
 * in the admin, so adding or removing a network never needs a deploy.
 */
export const SOCIAL_NETWORKS = ['facebook', 'instagram', 'x', 'youtube', 'linkedin', 'whatsapp', 'telegram', 'other'] as const;
export type SocialNetwork = (typeof SOCIAL_NETWORKS)[number];

export const SOCIAL_NETWORK_LABELS: Record<SocialNetwork, string> = {
  facebook: 'Facebook',
  instagram: 'Instagram',
  x: 'X (Twitter)',
  youtube: 'YouTube',
  linkedin: 'LinkedIn',
  whatsapp: 'WhatsApp',
  telegram: 'Telegram',
  other: 'Other link',
};

export const SOCIAL_MAX_LINKS = 10;
export const SOCIAL_LABEL_MAX = 24;

/** "instagram.com/meshsmith" -> "https://instagram.com/meshsmith". Anything with a scheme is left as typed. */
export function withHttps(input: string): string {
  const s = input.trim();
  if (!s) return s;
  return /^[a-z][a-z0-9+.-]*:/i.test(s) ? s : `https://${s.replace(/^\/+/, '')}`;
}

const webUrl = z
  .string()
  .trim()
  .max(300, 'That link is too long')
  .refine((u) => {
    try {
      const url = new URL(u);
      return (url.protocol === 'https:' || url.protocol === 'http:') && url.hostname.includes('.');
    } catch {
      return false;
    }
  }, 'Enter a full web link, like https://instagram.com/yourname');

export const socialLinkSchema = z.object({
  network: z.enum(SOCIAL_NETWORKS),
  url: webUrl,
  /** only for "other": the name shown on hover and read out by screen readers */
  label: z.string().trim().max(SOCIAL_LABEL_MAX).optional(),
});

export const socialLinksSchema = z
  .object({ links: z.array(socialLinkSchema).max(SOCIAL_MAX_LINKS) })
  .superRefine((value, ctx) => {
    const seen = new Set<string>();
    value.links.forEach((l, i) => {
      if (l.network === 'other') {
        if (!l.label) ctx.addIssue({ code: 'custom', path: ['links', i, 'label'], message: 'Give this link a name, like Pinterest' });
        return;
      }
      if (seen.has(l.network)) ctx.addIssue({ code: 'custom', path: ['links', i, 'network'], message: `${SOCIAL_NETWORK_LABELS[l.network]} is already in the list` });
      seen.add(l.network);
    });
  });

export type SocialLinkInput = z.infer<typeof socialLinkSchema>;
export type SocialLinksInput = z.infer<typeof socialLinksSchema>;

export interface SocialLinkDTO {
  network: SocialNetwork;
  url: string;
  label?: string;
}
